import React from 'react';
import { cn } from '@/shared/utils/cn';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  errorClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helpText,
      leftIcon,
      rightIcon,
      containerClassName,
      labelClassName,
      inputClassName,
      errorClassName,
      required,
      ...props
    },
    ref
  ) => {
    const hasError = !!error;
    const hasLeftIcon = !!leftIcon;
    const hasRightIcon = !!rightIcon;

    const inputClasses = cn(
      'block w-full rounded-lg border transition-colors duration-200 bg-white',
      'text-sm leading-5 py-2 pl-3 text-gray-900',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      'focus:outline-none focus:ring-2 focus:ring-offset-0',
      hasError
        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200',
      hasLeftIcon && 'pl-10',
      hasRightIcon && 'pr-10',
      !hasLeftIcon && 'pl-3',
      !hasRightIcon && 'pr-3',
      inputClassName,
      className
    );

    return (
      <div className={cn('space-y-1', containerClassName)}>
        {label && (
          <label
            className={cn(
              'block text-xs font-medium text-gray-700',
              labelClassName
            )}
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {hasLeftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-0">
              <div className="text-gray-400 ml-2">{leftIcon}</div>
            </div>
          )}

          <input className={inputClasses} ref={ref} {...props} />

          {hasRightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-0">
              <div className="text-gray-400 mr-2">{rightIcon}</div>
            </div>
          )}
        </div>

        {helpText && !hasError && (
          <p className="text-xs text-gray-500 mt-0.5">
            {helpText}
          </p>
        )}
        {hasError && (
          <p className={cn('text-xs text-error-600', errorClassName)}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
export default Input;
